import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const MAX_CHARS = 2000;

const STUB_RESPONSES = [
  "I'm reviewing the portfolio data now. Based on the current metrics, Solano Wind Farm is tracking above the acquisition case IRR at 12.4%. Would you like a deeper breakdown?",
  "The Tyne Bridge IC submission is pending 2 of 4 committee votes. James Donovan and Rachel Moore have not yet voted. The meeting is scheduled for 13 April.",
  "Fund I has deployed £153.7m across 5 assets. Fund II is currently in the capital raise phase with £140m soft-circled from Nordic Pension Fund and ADIA.",
  "I can help you draft a committee memo, summarise a review pack, or pull performance data for a specific asset. What would you like to focus on?",
];

// Glass design tokens — isolated to this component only.
// No SeedDrive CSS variables are referenced inside the panel.
const G = {
  panelBg:          'rgba(10, 6, 22, 0.88)',
  border:           'rgba(139, 92, 246, 0.20)',
  borderFaint:      'rgba(139, 92, 246, 0.11)',
  panelShadow:      '0 0 0 1px rgba(139, 92, 246, 0.14), 0 0 52px rgba(139, 92, 246, 0.16), 0 28px 72px rgba(0, 0, 0, 0.70)',
  headerBg:         'linear-gradient(180deg, rgba(139, 92, 246, 0.10) 0%, transparent 100%)',
  textPrimary:      'rgba(255, 255, 255, 0.92)',
  textSecondary:    'rgba(255, 255, 255, 0.46)',
  textMuted:        'rgba(255, 255, 255, 0.28)',
  userBubble:       'linear-gradient(135deg, #9f7aea 0%, #7c3aed 100%)',
  asstBubbleBg:     'rgba(255, 255, 255, 0.07)',
  asstBubbleBorder: 'rgba(255, 255, 255, 0.09)',
  inputBg:          'rgba(255, 255, 255, 0.05)',
  inputBorder:      'rgba(139, 92, 246, 0.24)',
  inputFocusBorder: 'rgba(139, 92, 246, 0.55)',
  inputFocusRing:   'rgba(139, 92, 246, 0.14)',
  toolbarIcon:      'rgba(255, 255, 255, 0.36)',
  toolbarHoverBg:   'rgba(139, 92, 246, 0.15)',
  toolbarHoverIcon: 'rgba(196, 181, 253, 0.90)',
  sendBg:           'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
  sendGlow:         '0 0 14px rgba(139, 92, 246, 0.50)',
  kbdBg:            'rgba(255, 255, 255, 0.07)',
  kbdBorder:        'rgba(255, 255, 255, 0.13)',
  successDot:       '#34d399',
  dotPulse:         'rgba(139, 92, 246, 0.80)',
  betaBg:           'rgba(139, 92, 246, 0.22)',
  betaBorder:       'rgba(139, 92, 246, 0.35)',
  betaText:         'rgba(196, 181, 253, 0.92)',
  fabBg:            'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
  fabOpen:          '0 0 0 2px rgba(139, 92, 246, 0.28), 0 6px 24px rgba(0,0,0,0.45)',
};

const COMPOSER_ACTIONS = [
  { icon: 'Paperclip', label: 'Attach file' },
  { icon: 'Link',      label: 'Insert link' },
  { icon: 'Code',      label: 'Insert code' },
  { icon: 'Layers',    label: 'Attach design' },
  { icon: 'Mic',       label: 'Voice input' },
];

export default function AIChatAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [textareaFocused, setTextareaFocused] = useState(false);
  const chatRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  const charCount = message.length;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  useEffect(() => {
    if (isOpen) setTimeout(() => textareaRef.current?.focus(), 150);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        chatRef.current &&
        !chatRef.current.contains(e.target) &&
        !e.target.closest('.ai-fab-button')
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSend = () => {
    const text = message.trim();
    if (!text || isThinking) return;
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setMessage('');
    setIsThinking(true);
    setTimeout(() => {
      const stub = STUB_RESPONSES[Math.floor(Math.random() * STUB_RESPONSES.length)];
      setMessages((prev) => [...prev, { role: 'assistant', text: stub }]);
      setIsThinking(false);
    }, 900);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !!message.trim() && !isThinking;

  return (
    <div className="fixed bottom-6 z-50" style={{ right: '5.5rem' }}>

      {/* FAB */}
      <button
        className={`ai-fab-button flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 active:scale-95 ${!isOpen ? 'ai-fab-glow-pulse' : ''}`}
        style={{
          background: G.fabBg,
          boxShadow: isOpen ? G.fabOpen : undefined,
          border: 'none',
          cursor: 'pointer',
        }}
        onClick={() => setIsOpen((p) => !p)}
        aria-label="AI Assistant"
      >
        <Icon name={isOpen ? 'X' : 'Bot'} size={20} color="white" strokeWidth={2} />
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={chatRef}
            initial={{ opacity: 0, scale: 0.91, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.91, y: 18 }}
            transition={{ duration: 0.24, ease: [0.175, 0.885, 0.32, 1.05] }}
            style={{
              position: 'absolute',
              bottom: '64px',
              right: 0,
              width: '320px',
              transformOrigin: 'bottom right',
              borderRadius: '20px',
              background: G.panelBg,
              backdropFilter: 'blur(28px) saturate(160%)',
              WebkitBackdropFilter: 'blur(28px) saturate(160%)',
              border: `1px solid ${G.border}`,
              boxShadow: G.panelShadow,
              overflow: 'hidden',
            }}
          >

            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '13px 16px',
                borderBottom: `1px solid ${G.borderFaint}`,
                background: G.headerBg,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Status dot */}
                <span
                  className="animate-pulse"
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: G.successDot,
                    boxShadow: `0 0 7px ${G.successDot}`,
                    flexShrink: 0,
                  }}
                />
                <span
                  className="font-caption"
                  style={{ fontSize: '0.8125rem', fontWeight: 600, color: G.textPrimary, letterSpacing: '0.01em' }}
                >
                  AI Assistant
                </span>
                <span
                  className="font-caption"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.07em',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: G.betaBg,
                    color: G.betaText,
                    border: `1px solid ${G.betaBorder}`,
                  }}
                >
                  BETA
                </span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '7px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                aria-label="Close assistant"
              >
                <Icon name="X" size={13} color={G.textSecondary} />
              </button>
            </div>

            {/* Message history */}
            <div
              className="ai-chat-messages"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                padding: '14px 16px',
                overflowY: 'auto',
                maxHeight: '260px',
                minHeight: messages.length > 0 ? '80px' : '0',
              }}
            >
              {messages.length === 0 && (
                <p
                  className="font-caption"
                  style={{ fontSize: '0.75rem', color: G.textMuted, textAlign: 'center', padding: '8px 0', margin: 0 }}
                >
                  Ask about portfolio data, deals, or draft a memo.
                </p>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}
                >
                  <div
                    className="font-caption"
                    style={{
                      fontSize: '0.8125rem',
                      borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      padding: '8px 12px',
                      maxWidth: '85%',
                      lineHeight: 1.55,
                      ...(msg.role === 'user'
                        ? { background: G.userBubble, color: '#fff' }
                        : {
                            background: G.asstBubbleBg,
                            color: G.textPrimary,
                            border: `1px solid ${G.asstBubbleBorder}`,
                          }),
                    }}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}

              {isThinking && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div
                    style={{
                      borderRadius: '14px 14px 14px 4px',
                      padding: '10px 14px',
                      background: G.asstBubbleBg,
                      border: `1px solid ${G.asstBubbleBorder}`,
                      display: 'inline-flex',
                      gap: '5px',
                      alignItems: 'center',
                    }}
                  >
                    {[0, 150, 300].map((delay) => (
                      <span
                        key={delay}
                        className="animate-bounce"
                        style={{
                          width: '5px',
                          height: '5px',
                          borderRadius: '50%',
                          background: G.dotPulse,
                          display: 'inline-block',
                          animationDelay: `${delay}ms`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div
              style={{
                padding: '10px 16px 16px',
                borderTop: `1px solid ${G.borderFaint}`,
              }}
            >
              {/* Toolbar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '9px' }}>
                {COMPOSER_ACTIONS.map(({ icon, label }) => (
                  <button
                    key={icon}
                    title={label}
                    aria-label={label}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '7px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = G.toolbarHoverBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Icon name={icon} size={13} color={G.toolbarIcon} />
                  </button>
                ))}
              </div>

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
                onKeyDown={handleKeyDown}
                onFocus={() => setTextareaFocused(true)}
                onBlur={() => setTextareaFocused(false)}
                rows={3}
                disabled={isThinking}
                placeholder="Ask anything about the portfolio, a deal, or an asset…"
                className="ai-chat-placeholder font-caption"
                style={{
                  width: '100%',
                  background: G.inputBg,
                  border: `1px solid ${textareaFocused ? G.inputFocusBorder : G.inputBorder}`,
                  borderRadius: '10px',
                  padding: '9px 12px',
                  fontSize: '0.8125rem',
                  color: G.textPrimary,
                  lineHeight: 1.55,
                  resize: 'none',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxShadow: textareaFocused ? `0 0 0 3px ${G.inputFocusRing}` : 'none',
                  scrollbarWidth: 'none',
                }}
              />

              {/* Footer */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '9px' }}>
                <span
                  className="font-caption"
                  style={{ fontSize: '0.695rem', color: G.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <kbd
                    style={{
                      padding: '1px 5px',
                      borderRadius: '4px',
                      fontSize: '0.67rem',
                      fontFamily: 'monospace',
                      border: `1px solid ${G.kbdBorder}`,
                      background: G.kbdBg,
                      color: G.textSecondary,
                    }}
                  >
                    Shift+Enter
                  </kbd>
                  {' '}new line
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="font-caption" style={{ fontSize: '0.695rem', color: G.textMuted }}>
                    {charCount}/{MAX_CHARS}
                  </span>
                  <button
                    onClick={handleSend}
                    disabled={!canSend}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '9px',
                      border: 'none',
                      background: G.sendBg,
                      cursor: canSend ? 'pointer' : 'not-allowed',
                      opacity: canSend ? 1 : 0.32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'opacity 0.18s, transform 0.15s, box-shadow 0.15s',
                      boxShadow: canSend ? G.sendGlow : 'none',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => { if (canSend) e.currentTarget.style.transform = 'scale(1.07)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    aria-label="Send message"
                  >
                    <Icon name="Send" size={13} color="white" />
                  </button>
                </div>
              </div>
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
